// ColdOutreach.jsx
// Bulk outreach to cold lead lists (uploaded CSV / pasted from Excel).
// Auto-dedup on import: drops phones already in clients / leads / dnc_list.
// Sends a daily, AI-personalized SMS to N leads/day (default 250) via
// the cold-outreach-cron Edge Function. Replies are handled by the
// existing ai-receptionist (it already books, reschedules, etc.).

import { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed", purpleL:"rgba(124,58,237,0.10)",
  warm:"#f59e0b", warmL:"rgba(245,158,11,0.12)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {
  width:"100%", padding:"10px 12px", background:C.light,
  border:`1px solid ${C.border}`, borderRadius:8, fontSize:13,
  color:C.text, fontFamily:F, outline:"none", boxSizing:"border-box",
};

const normalizePhone = (p) => {
  const d = (p || "").replace(/\D/g, "");
  if (d.length === 10) return "+1" + d;
  if (d.length === 11 && d.startsWith("1")) return "+" + d;
  if (d.length > 0) return "+" + d;
  return "";
};

const parseCsv = (text) => {
  // Light CSV parser — handles commas, tabs, basic quoting. Good enough for
  // pasted spreadsheet rows + simple CSV files.
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter(l => l.trim());
  if (!lines.length) return [];
  // Detect delimiter
  const first = lines[0];
  const delim = first.includes("\t") ? "\t" : ",";
  const splitRow = (l) => {
    const cells = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < l.length; i++) {
      const ch = l[i];
      if (ch === '"') {
        if (inQ && l[i+1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (ch === delim && !inQ) {
        cells.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    cells.push(cur);
    return cells.map(c => c.trim());
  };
  const header = splitRow(lines[0]).map(h => h.toLowerCase().replace(/[^a-z0-9_]/g, "_").replace(/_+/g, "_"));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitRow(lines[i]);
    const row = {};
    header.forEach((h, idx) => { row[h] = cells[idx] || ""; });
    rows.push(row);
  }
  return { header, rows };
};

const extractFromRow = (row) => {
  // Try common column names
  const get = (...keys) => {
    for (const k of keys) {
      if (row[k]) return row[k];
    }
    return "";
  };
  let name = get("name", "full_name", "fullname", "client_name");
  const first = get("first_name", "firstname", "first");
  const last  = get("last_name", "lastname", "last");
  if (!name && (first || last)) name = `${first} ${last}`.trim();
  const phone = get("phone", "phone_number", "mobile", "cell", "tel");
  const email = get("email", "e_mail", "email_address");
  return {
    full_name: name || null,
    first_name: first || (name ? name.split(" ")[0] : null) || null,
    last_name: last || (name ? name.split(" ").slice(1).join(" ") : null) || null,
    phone: normalizePhone(phone),
    email: email || null,
  };
};

export default function ColdOutreach() {
  const [tab, setTab] = useState("campaigns");

  return (
    <div>
      <div style={{marginBottom:18}}>
        <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S}}>📣 Cold Outreach Campaigns</div>
        <div style={{fontSize:12,color:C.muted,marginTop:4}}>
          Upload cold lead lists, auto-dedup against existing clients & leads, and let the AI bot reach out to N leads per day. Replies are handled by Sophia automatically.
        </div>
      </div>

      <div style={{display:"flex",gap:4,background:C.light,padding:3,borderRadius:9,border:`1px solid ${C.border}`,width:"fit-content",marginBottom:18,flexWrap:"wrap"}}>
        {[
          ["campaigns", "📊 Campaigns"],
          ["new", "➕ New Campaign"],
        ].map(([v, l]) => (
          <button key={v} onClick={()=>setTab(v)} style={{
            padding:"8px 18px", fontSize:13, fontWeight:tab===v?800:600,
            color:tab===v?"#fff":C.muted,
            background:tab===v?`linear-gradient(135deg,${C.gold},${C.goldD})`:"none",
            borderRadius:7, border:"none", cursor:"pointer", fontFamily:F,
          }}>{l}</button>
        ))}
      </div>

      {tab === "campaigns" && <CampaignsTab onCreate={()=>setTab("new")}/>}
      {tab === "new" && <NewCampaignTab onDone={()=>setTab("campaigns")}/>}
    </div>
  );
}

function CampaignsTab({ onCreate }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from("cold_outreach_campaigns")
      .select("*").order("created_at", { ascending: false });
    setCampaigns(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase.channel("coldout-campaigns")
      .on("postgres_changes", { event:"*", schema:"public", table:"cold_outreach_campaigns" }, load)
      .on("postgres_changes", { event:"*", schema:"public", table:"cold_outreach_recipients" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const togglePause = async (c) => {
    const next = !c.paused;
    // Optimistic update so the user sees instant feedback (the realtime
    // sub also kicks in but can take a beat)
    setCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, paused: next } : x));
    const { error } = await supabase.from("cold_outreach_campaigns").update({ paused: next }).eq("id", c.id);
    if (error) {
      alert("Failed to " + (next ? "pause" : "resume") + ": " + error.message);
      // Revert
      setCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, paused: c.paused } : x));
      return;
    }
    // Force a refresh
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"? All recipients will be removed too.`)) return;
    await supabase.from("cold_outreach_campaigns").delete().eq("id", c.id);
  };

  const runNow = async (c) => {
    if (!window.confirm(`Force-run "${c.name}" now? Will send up to ${c.daily_limit} messages immediately. Use only for testing!`)) return;
    await supabase.functions.invoke("cold-outreach-cron", { body: { force: 1, campaign_id: c.id } });
    alert("Run triggered. Refresh in a few minutes to see progress.");
  };

  if (loading) return <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:13}}>Loading…</div>;

  if (campaigns.length === 0) {
    return (
      <div style={{padding:60,textAlign:"center",background:C.light,borderRadius:12,border:"1px dashed "+C.border}}>
        <div style={{fontSize:48,marginBottom:10,opacity:0.4}}>📣</div>
        <div style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:6}}>No campaigns yet</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Upload your first lead list to start sending</div>
        <button onClick={onCreate} style={{padding:"10px 20px",background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ Create Campaign</button>
      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {campaigns.map(c => <CampaignCard key={c.id} campaign={c} onPause={()=>togglePause(c)} onDelete={()=>remove(c)} onRunNow={()=>runNow(c)}/>)}
    </div>
  );
}

function CampaignCard({ campaign, onPause, onDelete, onRunNow }) {
  const [stats, setStats] = useState({ queued: 0, sent: 0, replied: 0, booked: 0, skipped: 0, failed: 0 });
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    const load = async () => {
      // Use exact counts (head:true) — Supabase default range caps at 1000
      // rows so a plain select() under-counts campaigns with > 1k recipients
      // (this campaign has ~6k). Six count queries → instant + accurate.
      const countBy = async (statusVal, eligibleVal) => {
        let q = supabase.from("cold_outreach_recipients")
          .select("id", { count: "exact", head: true })
          .eq("campaign_id", campaign.id);
        if (statusVal !== undefined) q = q.eq("status", statusVal);
        if (eligibleVal !== undefined) q = q.eq("eligible", eligibleVal);
        const { count } = await q;
        return count || 0;
      };
      const [queued, sent, replied, booked, failed, skipped] = await Promise.all([
        countBy("queued", true),
        countBy("sent"),
        countBy("replied"),
        countBy("booked"),
        countBy("failed"),
        countBy(undefined, false),  // skipped = ineligible
      ]);
      setStats({ queued, sent, replied, booked, failed, skipped });
    };
    load();
    const ch = supabase.channel("coldout-stats-"+campaign.id)
      .on("postgres_changes", { event:"*", schema:"public", table:"cold_outreach_recipients", filter:"campaign_id=eq."+campaign.id }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [campaign.id]);

  const eligibleTotal = stats.queued + stats.sent + stats.replied + stats.booked + stats.failed;
  const progress = eligibleTotal > 0 ? Math.round(((stats.sent + stats.replied + stats.booked) / eligibleTotal) * 100) : 0;

  return (
    <>
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderLeft:`5px solid ${campaign.paused?C.warm:C.green}`,borderRadius:12,padding:18}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,gap:12,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:S,marginBottom:4}}>{campaign.name}</div>
          <div style={{fontSize:12,color:C.muted}}>{campaign.offer_label}</div>
          <div style={{fontSize:10,color:C.dim,marginTop:4}}>
            Created {new Date(campaign.created_at).toLocaleDateString()} ·
            {campaign.daily_limit}/day at {campaign.send_hour_et}:00 ET
          </div>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <span style={{padding:"4px 10px",background:campaign.paused?C.warmL:C.greenL,color:campaign.paused?C.warm:C.green,borderRadius:50,fontSize:10,fontWeight:800,letterSpacing:"0.05em",textTransform:"uppercase"}}>
            {campaign.paused ? "⏸ Paused" : "▶ Active"}
          </span>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(110px,1fr))",gap:8,marginBottom:14}}>
        <StatBox label="Queued"  value={stats.queued}  color={C.blue}/>
        <StatBox label="Sent"    value={stats.sent}    color={C.gold}/>
        <StatBox label="Replied" value={stats.replied} color={C.purple}/>
        <StatBox label="Booked"  value={stats.booked}  color={C.green}/>
        <StatBox label="Skipped (dedup)" value={stats.skipped} color={C.muted}/>
        {stats.failed > 0 && <StatBox label="Failed"  value={stats.failed} color={C.red}/>}
      </div>

      {eligibleTotal > 0 && (
        <div style={{marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.muted,marginBottom:5}}>
            <span>Progress</span><span>{progress}%</span>
          </div>
          <div style={{height:8,background:C.light,borderRadius:4,overflow:"hidden"}}>
            <div style={{height:"100%",width:progress+"%",background:`linear-gradient(90deg,${C.gold},${C.goldD})`,transition:"width 0.3s"}}/>
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        <button onClick={()=>setShowDetail(true)} style={{padding:"7px 14px",background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>
          📊 Open Report & Preview
        </button>
        <button onClick={onPause} style={{padding:"7px 14px",background:campaign.paused?C.greenL:C.warmL,color:campaign.paused?C.green:C.warm,border:`1px solid ${(campaign.paused?C.green:C.warm)}40`,borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>
          {campaign.paused ? "▶ Resume" : "⏸ Pause"}
        </button>
        <button onClick={onRunNow} style={{padding:"7px 14px",background:C.blueL,color:C.blue,border:`1px solid ${C.blue}40`,borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>
          ⚡ Run now (test)
        </button>
        <button onClick={onDelete} style={{padding:"7px 14px",background:C.redL,color:C.red,border:`1px solid ${C.red}40`,borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,marginLeft:"auto"}}>🗑 Delete</button>
      </div>
    </div>

    {showDetail && <CampaignDetailModal campaign={campaign} onClose={()=>setShowDetail(false)} onPause={onPause} onRunNow={onRunNow}/>}
    </>
  );
}

function CampaignDetailModal({ campaign, onClose, onPause, onRunNow }) {
  // tab: 'overview' | 'messages' | 'recipients' | 'schedule'
  const [tab, setTab] = useState("overview");

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:920,maxHeight:"94vh",overflow:"auto",position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:14,right:16,background:C.light,border:"none",borderRadius:7,padding:"5px 11px",cursor:"pointer",fontSize:18,color:C.muted,zIndex:1}}>×</button>

        {/* Header */}
        <div style={{padding:"22px 26px 14px",borderBottom:`1px solid ${C.border}`,background:`linear-gradient(135deg,${C.goldBg},#fff)`}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:4}}>📣 Cold Outreach Campaign</div>
          <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S,marginBottom:6}}>{campaign.name}</div>
          <div style={{fontSize:13,color:C.muted}}>{campaign.offer_label}</div>
          <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}}>
            <button onClick={onPause} style={{padding:"7px 16px",background:campaign.paused?`linear-gradient(135deg,${C.green},#1e6b3f)`:C.warmL,color:campaign.paused?"#fff":C.warm,border:campaign.paused?"none":`1px solid ${C.warm}40`,borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>
              {campaign.paused ? "▶ Resume Campaign" : "⏸ Pause Campaign"}
            </button>
            <button onClick={onRunNow} style={{padding:"7px 16px",background:C.blueL,color:C.blue,border:`1px solid ${C.blue}40`,borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>
              ⚡ Run now (test)
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{display:"flex",gap:4,padding:"12px 22px 0",borderBottom:`1px solid ${C.border}`,flexWrap:"wrap"}}>
          {[["overview","📊 Overview & Report"],["messages","💬 Message Preview"],["recipients","👥 Recipients"],["schedule","⏰ Daily Schedule"]].map(([v,l])=>(
            <button key={v} onClick={()=>setTab(v)} style={{
              padding:"10px 16px",fontSize:12,fontWeight:tab===v?800:600,
              color:tab===v?C.gold:C.muted,
              background:"none",
              border:"none",
              borderBottom:tab===v?`2px solid ${C.gold}`:"2px solid transparent",
              cursor:"pointer",fontFamily:F,
            }}>{l}</button>
          ))}
        </div>

        <div style={{padding:"22px 26px"}}>
          {tab === "overview" && <OverviewTab campaign={campaign}/>}
          {tab === "messages" && <MessagesTab campaign={campaign}/>}
          {tab === "recipients" && <RecipientsTab campaign={campaign}/>}
          {tab === "schedule" && <ScheduleTab campaign={campaign}/>}
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ campaign }) {
  // Range modes — quick presets + custom range + single day
  const [mode, setMode] = useState("today"); // 'today' | 'yesterday' | 'week' | 'month' | 'all' | 'single' | 'range'
  const todayET = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  const yesterdayET = new Date(Date.now() - 86400000).toLocaleDateString("en-CA", { timeZone: "America/New_York" });

  const [singleDate, setSingleDate] = useState(todayET);
  const [from, setFrom] = useState(new Date(Date.now() - 6 * 86400000).toLocaleDateString("en-CA", { timeZone: "America/New_York" }));
  const [to, setTo] = useState(todayET);

  const [stats, setStats] = useState(null);
  const [byDay, setByDay] = useState([]);
  const [lifetimeStats, setLifetimeStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine effective range from current mode
  const effectiveRange = useMemo(() => {
    if (mode === "today")     return { from: todayET, to: todayET, label: `Today (${formatDateLabel(todayET)})` };
    if (mode === "yesterday") return { from: yesterdayET, to: yesterdayET, label: `Yesterday (${formatDateLabel(yesterdayET)})` };
    if (mode === "week")      return { from: new Date(Date.now() - 6 * 86400000).toLocaleDateString("en-CA", { timeZone: "America/New_York" }), to: todayET, label: "Last 7 days" };
    if (mode === "month")     return { from: new Date(Date.now() - 29 * 86400000).toLocaleDateString("en-CA", { timeZone: "America/New_York" }), to: todayET, label: "Last 30 days" };
    if (mode === "all")       return { from: null, to: null, label: "All time" };
    if (mode === "single")    return { from: singleDate, to: singleDate, label: `${formatDateLabel(singleDate)}` };
    if (mode === "range")     return { from, to, label: `${formatDateLabel(from)} → ${formatDateLabel(to)}` };
    return { from: null, to: null, label: "All time" };
  }, [mode, singleDate, from, to, todayET, yesterdayET]);

  // Compute stats whenever range changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);

      // Build the base query for the range
      const baseFilter = (q) => {
        q = q.eq("campaign_id", campaign.id);
        if (effectiveRange.from) q = q.gte("scheduled_send_date", effectiveRange.from);
        if (effectiveRange.to)   q = q.lte("scheduled_send_date", effectiveRange.to);
        if (!effectiveRange.from) q = q.not("scheduled_send_date", "is", null);
        return q;
      };

      const countStatus = async (status) => {
        let q = supabase.from("cold_outreach_recipients").select("id", { count: "exact", head: true }).eq("status", status);
        q = baseFilter(q);
        const { count } = await q;
        return count || 0;
      };

      const [sent, replied, booked, failed] = await Promise.all([
        countStatus("sent"),
        countStatus("replied"),
        countStatus("booked"),
        countStatus("failed"),
      ]);

      // Lifetime totals (everything since campaign start) for the "vs lifetime" line
      const lifetimeCount = async (status) => {
        const { count } = await supabase.from("cold_outreach_recipients")
          .select("id", { count: "exact", head: true })
          .eq("campaign_id", campaign.id)
          .eq("status", status);
        return count || 0;
      };
      const [lSent, lReplied, lBooked] = await Promise.all([
        lifetimeCount("sent"),
        lifetimeCount("replied"),
        lifetimeCount("booked"),
      ]);

      // Per-day breakdown for the chart (only if range covers > 1 day)
      let daySeries = [];
      const showChart = effectiveRange.from && effectiveRange.to && effectiveRange.from !== effectiveRange.to;
      if (showChart) {
        // Pull rows for the period (status in active set), aggregate by day
        const { data: rangeRows } = await supabase.from("cold_outreach_recipients")
          .select("status,scheduled_send_date")
          .eq("campaign_id", campaign.id)
          .gte("scheduled_send_date", effectiveRange.from)
          .lte("scheduled_send_date", effectiveRange.to)
          .not("scheduled_send_date", "is", null)
          .in("status", ["sent","replied","booked"]);
        const dayMap = {};
        for (const r of rangeRows || []) {
          dayMap[r.scheduled_send_date] = dayMap[r.scheduled_send_date] || { date: r.scheduled_send_date, sent: 0, replied: 0, booked: 0 };
          if (r.status === "sent") dayMap[r.scheduled_send_date].sent++;
          if (r.status === "replied") dayMap[r.scheduled_send_date].replied++;
          if (r.status === "booked") dayMap[r.scheduled_send_date].booked++;
        }
        daySeries = Object.values(dayMap).sort((a,b) => a.date.localeCompare(b.date));
      }

      if (cancelled) return;
      setStats({ sent, replied, booked, failed });
      setLifetimeStats({ sent: lSent, replied: lReplied, booked: lBooked });
      setByDay(daySeries);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [campaign.id, effectiveRange]);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {/* Range picker — quick buttons + single date + custom range */}
      <div>
        <div style={{fontSize:13,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:10,letterSpacing:"0.04em"}}>📅 PICK A PERIOD</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
          {[
            ["today","Today"],
            ["yesterday","Yesterday"],
            ["week","Last 7 days"],
            ["month","Last 30 days"],
            ["all","All time"],
            ["single","📆 Pick a day"],
            ["range","📆 Custom range"],
          ].map(([v,l]) => (
            <button key={v} onClick={()=>setMode(v)} style={{
              padding:"7px 13px",fontSize:11,fontWeight:mode===v?800:600,
              color:mode===v?"#fff":C.muted,
              background:mode===v?`linear-gradient(135deg,${C.gold},${C.goldD})`:C.light,
              border:`1px solid ${mode===v?C.gold:C.border}`,
              borderRadius:7,cursor:"pointer",fontFamily:F,
            }}>{l}</button>
          ))}
        </div>
        {mode === "single" && (
          <div style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:C.muted}}>
            <span>Show stats for:</span>
            <input type="date" value={singleDate} onChange={e=>setSingleDate(e.target.value)} style={{...inp,padding:"6px 9px",width:"auto",fontSize:12}}/>
          </div>
        )}
        {mode === "range" && (
          <div style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:C.muted,flexWrap:"wrap"}}>
            <span>From</span>
            <input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={{...inp,padding:"6px 9px",width:"auto",fontSize:12}}/>
            <span>to</span>
            <input type="date" value={to} onChange={e=>setTo(e.target.value)} style={{...inp,padding:"6px 9px",width:"auto",fontSize:12}}/>
          </div>
        )}
      </div>

      {/* Report card for the selected range */}
      <div style={{background:`linear-gradient(135deg,${C.goldBg},#fff)`,border:`1px solid ${C.borderGold}`,borderRadius:12,padding:"20px 22px"}}>
        <div style={{fontSize:11,color:C.gold,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:4}}>📊 Report</div>
        <div style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:S,marginBottom:18}}>{effectiveRange.label}</div>

        {loading ? (
          <div style={{padding:20,textAlign:"center",color:C.muted,fontSize:13}}>Loading report…</div>
        ) : (
          <>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:14}}>
              <BigStat label="📤 Sent"      value={stats.sent}    color={C.gold}/>
              <BigStat label="💬 Replied"   value={stats.replied} color={C.purple}/>
              <BigStat label="✅ Booked"    value={stats.booked}  color={C.green}/>
              {stats.failed > 0 && <BigStat label="❌ Failed" value={stats.failed} color={C.red}/>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10}}>
              <RateRow label="Reply rate" pct={stats.sent>0 ? Math.round((stats.replied/stats.sent)*100) : null} color={C.blue}/>
              <RateRow label="Book rate"  pct={stats.sent>0 ? Math.round((stats.booked/stats.sent)*100) : null} color={C.green}/>
              <RateRow label="Reply → Book" pct={stats.replied>0 ? Math.round((stats.booked/stats.replied)*100) : null} color={C.purple}/>
            </div>
          </>
        )}
      </div>

      {/* Per-day chart only when range is > 1 day */}
      {byDay.length > 1 && (
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 16px"}}>
          <div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:12,textTransform:"uppercase",letterSpacing:"0.08em"}}>Activity by day</div>
          <div style={{display:"flex",alignItems:"flex-end",gap:6,height:140,overflow:"auto",padding:"0 4px"}}>
            {byDay.map(d => {
              const max = Math.max(...byDay.map(x => x.sent + x.replied + x.booked), 1);
              const sentH = Math.round((d.sent / max) * 100);
              const repH  = Math.round((d.replied / max) * 100);
              const bookH = Math.round((d.booked / max) * 100);
              return (
                <div key={d.date} title={`${d.date}: ${d.sent} sent · ${d.replied} replied · ${d.booked} booked`} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,minWidth:42,cursor:"pointer"}} onClick={()=>{setMode("single"); setSingleDate(d.date);}}>
                  <div style={{display:"flex",alignItems:"flex-end",gap:2,height:110}}>
                    <div style={{width:10,height:sentH+"%",background:C.gold,borderRadius:2,minHeight:d.sent>0?2:0}}/>
                    <div style={{width:10,height:repH+"%",background:C.purple,borderRadius:2,minHeight:d.replied>0?2:0}}/>
                    <div style={{width:10,height:bookH+"%",background:C.green,borderRadius:2,minHeight:d.booked>0?2:0}}/>
                  </div>
                  <div style={{fontSize:9,color:C.muted,whiteSpace:"nowrap"}}>{new Date(d.date+"T12:00:00").toLocaleDateString("en-US",{month:"numeric",day:"numeric"})}</div>
                </div>
              );
            })}
          </div>
          <div style={{display:"flex",gap:14,marginTop:12,fontSize:10,color:C.muted,flexWrap:"wrap"}}>
            <Legend label="Sent" color={C.gold}/>
            <Legend label="Replied" color={C.purple}/>
            <Legend label="Booked" color={C.green}/>
            <span style={{marginLeft:"auto",fontStyle:"italic",fontSize:9}}>Click any day for its detailed report</span>
          </div>
        </div>
      )}

      {/* Campaign lifetime totals */}
      {lifetimeStats && (
        <div style={{padding:"14px 16px",background:C.light,border:`1px solid ${C.border}`,borderRadius:10,fontSize:12,color:C.muted}}>
          <strong style={{color:C.text}}>📈 Lifetime totals</strong>: {lifetimeStats.sent.toLocaleString()} sent · {lifetimeStats.replied.toLocaleString()} replied · {lifetimeStats.booked.toLocaleString()} booked
          {lifetimeStats.sent > 0 && (
            <span> · {Math.round((lifetimeStats.replied/lifetimeStats.sent)*100)}% reply rate · {Math.round((lifetimeStats.booked/lifetimeStats.sent)*100)}% book rate</span>
          )}
        </div>
      )}
    </div>
  );
}

function BigStat({ label, value, color }) {
  return (
    <div style={{padding:"14px 16px",background:"#fff",border:`1px solid ${color}30`,borderRadius:10,textAlign:"center"}}>
      <div style={{fontSize:11,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>{label}</div>
      <div style={{fontSize:28,fontWeight:900,color,fontFamily:S,lineHeight:1}}>{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}

function RateRow({ label, pct, color }) {
  return (
    <div style={{padding:"10px 14px",background:"#fff",border:`1px solid ${C.border}`,borderRadius:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <span style={{fontSize:11,color:C.muted,fontWeight:600}}>{label}</span>
      <span style={{fontSize:15,fontWeight:800,color,fontFamily:S}}>{pct === null ? "—" : pct + "%"}</span>
    </div>
  );
}

function Legend({ label, color }) {
  return <span><span style={{display:"inline-block",width:10,height:10,background:color,marginRight:5,verticalAlign:"middle",borderRadius:2}}/>{label}</span>;
}

function formatDateLabel(d) {
  if (!d) return "";
  return new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function ScheduleTab({ campaign }) {
  // Per-day-of-week send times in America/New_York. Keys: sun mon tue wed thu fri sat
  const initial = campaign.daily_schedule || {};
  const [sched, setSched] = useState({
    sun: initial.sun || "10:00",
    mon: initial.mon || "10:00",
    tue: initial.tue || "10:00",
    wed: initial.wed || "10:00",
    thu: initial.thu || "10:00",
    fri: initial.fri || "10:00",
    sat: initial.sat || "10:00",
  });
  const [enabled, setEnabled] = useState({
    sun: !!initial.sun,
    mon: !!initial.mon,
    tue: !!initial.tue,
    wed: !!initial.wed,
    thu: !!initial.thu,
    fri: !!initial.fri,
    sat: !!initial.sat,
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const days = [
    ["sun", "Sunday",    "ראשון"],
    ["mon", "Monday",    "שני"],
    ["tue", "Tuesday",   "שלישי"],
    ["wed", "Wednesday", "רביעי"],
    ["thu", "Thursday",  "חמישי"],
    ["fri", "Friday",    "שישי"],
    ["sat", "Saturday",  "שבת"],
  ];

  const save = async () => {
    setSaving(true);
    try {
      // Build the schedule object — omit days that are disabled
      const finalSched = {};
      for (const [k] of days) {
        if (enabled[k] && sched[k]) finalSched[k] = sched[k];
      }
      const { error } = await supabase.from("cold_outreach_campaigns")
        .update({ daily_schedule: Object.keys(finalSched).length > 0 ? finalSched : null })
        .eq("id", campaign.id);
      if (error) throw error;
      setSavedAt(new Date());
    } catch (e) {
      alert("Save failed: " + (e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const fmt12 = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const hh = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hh}:${String(m).padStart(2,"0")} ${h >= 12 ? "PM" : "AM"}`;
  };

  // Today's day key (for highlighting)
  const todayKey = new Date().toLocaleDateString("en-US", { weekday: "short", timeZone: "America/New_York" }).toLowerCase();

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div>
        <div style={{fontSize:13,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:6,letterSpacing:"0.04em"}}>⏰ Daily Send Schedule</div>
        <div style={{fontSize:11,color:C.muted,lineHeight:1.6}}>
          Choose what time the daily 250 messages go out for each day of the week.<br/>
          All times are <strong>America/New_York</strong> (Eastern). Uncheck a day to skip it entirely.
        </div>
      </div>

      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
        {days.map(([key, en, he], i) => {
          const isToday = todayKey === key;
          return (
            <div key={key} style={{
              padding:"14px 16px",
              borderBottom: i < 6 ? `1px solid ${C.border}` : "none",
              background: isToday ? C.goldBg : "transparent",
              display:"flex",
              alignItems:"center",
              gap:14,
              flexWrap:"wrap",
            }}>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",minWidth:160,flex:"0 0 auto"}}>
                <input
                  type="checkbox"
                  checked={enabled[key]}
                  onChange={e=>setEnabled({...enabled, [key]: e.target.checked})}
                  style={{accentColor:C.gold,width:16,height:16}}
                />
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.text}}>{en} {isToday && <span style={{fontSize:9,padding:"1px 6px",background:C.gold,color:"#fff",borderRadius:6,marginLeft:4,fontWeight:800}}>TODAY</span>}</div>
                  <div style={{fontSize:10,color:C.muted}}>{he}</div>
                </div>
              </label>
              <div style={{display:"flex",alignItems:"center",gap:8,flex:1}}>
                <input
                  type="time"
                  value={sched[key]}
                  onChange={e=>setSched({...sched, [key]: e.target.value})}
                  disabled={!enabled[key]}
                  style={{...inp, width:140, padding:"8px 10px", fontSize:13, opacity: enabled[key] ? 1 : 0.4}}
                />
                <span style={{fontSize:12,color:C.muted,fontWeight:600,minWidth:90}}>
                  {enabled[key] ? fmt12(sched[key]) + " ET" : "⏸ Skipped"}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{padding:"12px 14px",background:C.blueL,border:`1px solid ${C.blue}30`,borderRadius:8,fontSize:11,color:C.blue,lineHeight:1.5}}>
        💡 <strong>How it works:</strong> the system fires every 5 minutes — when ET clock matches the day's target time (±5 min), 250 messages go out. Unchecked days are skipped entirely. To pause the campaign without losing the schedule, use the <strong>⏸ Pause Campaign</strong> button.
      </div>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
        <div style={{fontSize:11,color:C.muted}}>
          {savedAt && <span style={{color:C.green,fontWeight:600}}>✓ Saved at {savedAt.toLocaleTimeString()}</span>}
        </div>
        <button onClick={save} disabled={saving} style={{
          padding:"10px 22px",
          background: saving ? C.light : `linear-gradient(135deg,${C.gold},${C.goldD})`,
          color: saving ? C.muted : "#fff",
          border:"none",
          borderRadius:8,
          fontSize:13,
          fontWeight:800,
          cursor: saving ? "wait" : "pointer",
          fontFamily:F,
        }}>{saving ? "Saving..." : "💾 Save Schedule"}</button>
      </div>
    </div>
  );
}

function MessagesTab({ campaign }) {
  const [samples, setSamples] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("cold-outreach-preview", { body: { campaign_id: campaign.id } });
      if (error) throw error;
      setSamples(data);
    } catch (e) {
      alert("Preview failed: " + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  // Also load the actual sent messages from DB (last 10)
  const [recentSent, setRecentSent] = useState([]);
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from("cold_outreach_recipients")
        .select("first_name,full_name,phone,ai_message,sent_at")
        .eq("campaign_id", campaign.id)
        .not("ai_message", "is", null)
        .order("sent_at", { ascending: false })
        .limit(10);
      setRecentSent(data || []);
    };
    load();
    const ch = supabase.channel("coldout-recent-sent-"+campaign.id)
      .on("postgres_changes", { event:"*", schema:"public", table:"cold_outreach_recipients", filter:"campaign_id=eq."+campaign.id }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [campaign.id]);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:13,fontWeight:800,color:C.gold,fontFamily:S,letterSpacing:"0.04em"}}>👁 PREVIEW (3 fresh samples from AI)</div>
          <button onClick={generate} disabled={loading} style={{padding:"8px 16px",background:loading?C.light:`linear-gradient(135deg,${C.purple},#5b21b6)`,color:loading?C.muted:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:loading?"wait":"pointer",fontFamily:F}}>
            {loading ? "Generating…" : (samples ? "🔄 Regenerate" : "✨ Generate preview")}
          </button>
        </div>
        <div style={{fontSize:11,color:C.muted,marginBottom:12,fontStyle:"italic"}}>
          Sophia writes a unique message for each lead. Click "Generate" to see 3 fresh examples — every actual message will be slightly different.
        </div>
        {!samples && (
          <div style={{padding:30,textAlign:"center",background:C.light,border:`1px dashed ${C.border}`,borderRadius:10,color:C.muted,fontSize:12}}>
            Tap "Generate preview" to see what Sophia will text leads.
          </div>
        )}
        {samples && (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {samples.samples.map((s, i) => (
              <PhoneBubble key={i} name={s.name} text={s.message}/>
            ))}
            {samples.style === "fixed" && (
              <div style={{fontSize:11,color:C.muted,fontStyle:"italic"}}>(Fixed message — same text for all leads, just first_name swapped in)</div>
            )}
          </div>
        )}
      </div>

      {/* Real sent messages */}
      <div>
        <div style={{fontSize:13,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:10,letterSpacing:"0.04em"}}>📤 LAST 10 ACTUALLY SENT</div>
        {recentSent.length === 0 && (
          <div style={{padding:24,textAlign:"center",background:C.light,border:`1px dashed ${C.border}`,borderRadius:10,color:C.muted,fontSize:12}}>
            No messages sent yet. They'll appear here once the campaign runs.
          </div>
        )}
        {recentSent.length > 0 && (
          <div style={{display:"flex",flexDirection:"column",gap:10,maxHeight:380,overflowY:"auto"}}>
            {recentSent.map((r, i) => (
              <PhoneBubble key={i} name={r.first_name || r.full_name?.split(" ")[0] || "Lead"} phone={r.phone} text={r.ai_message} timestamp={r.sent_at}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PhoneBubble({ name, phone, text, timestamp }) {
  return (
    <div style={{background:"linear-gradient(135deg,#faf7f0,#fff)",border:`1px solid ${C.border}`,borderRadius:12,padding:"12px 14px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6,gap:8}}>
        <div style={{fontSize:11,fontWeight:700,color:C.gold,fontFamily:S}}>→ {name}{phone ? ` · ${phone}` : ""}</div>
        {timestamp && <div style={{fontSize:9,color:C.muted}}>{new Date(timestamp).toLocaleString()}</div>}
      </div>
      <div style={{padding:"10px 14px",background:C.gold+"15",border:`1px solid ${C.borderGold}`,borderRadius:14,borderBottomLeftRadius:4,fontSize:13,lineHeight:1.55,color:C.text,whiteSpace:"pre-wrap"}}>
        {text}
      </div>
    </div>
  );
}

function RecipientsTab({ campaign }) {
  const [filter, setFilter] = useState("queued");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      let q = supabase.from("cold_outreach_recipients")
        .select("id,full_name,first_name,phone,email,status,dedup_reason,sent_at,replied_at,booked_at,ai_message", { count: "exact" })
        .eq("campaign_id", campaign.id);
      if (filter === "skipped") q = q.eq("eligible", false);
      else if (filter !== "all") q = q.eq("status", filter);
      if (search) q = q.or(`full_name.ilike.%${search}%,phone.ilike.%${search}%`);
      q = q.order("id", { ascending: true }).limit(150);
      const { data, count } = await q;
      if (cancelled) return;
      setRows(data || []);
      setTotalCount(count || 0);
      setLoading(false);
    };
    const t = setTimeout(load, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [campaign.id, filter, search]);

  const filters = [
    ["queued",   "⏳ Queued"],
    ["sent",     "📤 Sent"],
    ["replied",  "💬 Replied"],
    ["booked",   "✅ Booked"],
    ["failed",   "❌ Failed"],
    ["skipped",  "🚫 Skipped (dedup)"],
    ["all",      "📋 All"],
  ];

  return (
    <div>
      <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
        {filters.map(([v,l]) => (
          <button key={v} onClick={()=>setFilter(v)} style={{
            padding:"6px 12px",fontSize:11,fontWeight:filter===v?800:600,
            color:filter===v?"#fff":C.muted,
            background:filter===v?`linear-gradient(135deg,${C.gold},${C.goldD})`:C.light,
            border:`1px solid ${filter===v?C.gold:C.border}`,
            borderRadius:7,cursor:"pointer",fontFamily:F,
          }}>{l}</button>
        ))}
      </div>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search name or phone…" style={{...inp,marginBottom:12,fontSize:12}}/>
      <div style={{fontSize:11,color:C.muted,marginBottom:8}}>
        {loading ? "Loading…" : `Showing ${rows.length} of ${totalCount.toLocaleString()} ${filter === "all" ? "recipients" : filter}`}
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:480,overflowY:"auto"}}>
        {rows.map(r => (
          <div key={r.id} style={{padding:"8px 12px",background:C.card,border:`1px solid ${C.border}`,borderRadius:7,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:700,color:C.text}}>{r.full_name || r.first_name || "—"}</div>
              <div style={{fontSize:11,color:C.muted}}>{r.phone}{r.email ? " · " + r.email : ""}</div>
              {r.ai_message && (
                <div style={{fontSize:11,color:C.muted,marginTop:4,fontStyle:"italic",padding:"4px 8px",background:C.light,borderRadius:5,maxWidth:520}}>
                  "{r.ai_message.length > 140 ? r.ai_message.slice(0,140) + "…" : r.ai_message}"
                </div>
              )}
            </div>
            <div style={{textAlign:"right",fontSize:9,color:C.muted}}>
              <div style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:50,display:"inline-block",background:statusColor(r.status,true),color:statusColor(r.status,false),textTransform:"uppercase",letterSpacing:"0.04em"}}>
                {r.status}{r.dedup_reason ? ` · ${r.dedup_reason}` : ""}
              </div>
              {r.sent_at && <div style={{marginTop:3}}>Sent {new Date(r.sent_at).toLocaleString()}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function statusColor(status, bg) {
  const palette = {
    queued: bg ? C.blueL : C.blue,
    sent: bg ? C.goldBg : C.gold,
    replied: bg ? C.purpleL : C.purple,
    booked: bg ? C.greenL : C.green,
    failed: bg ? C.redL : C.red,
    skipped: bg ? C.light : C.muted,
    opted_out: bg ? C.redL : C.red,
  };
  return palette[status] || (bg ? C.light : C.muted);
}

function StatBox({ label, value, color }) {
  return (
    <div style={{padding:"10px 12px",background:color+"10",border:`1px solid ${color}30`,borderRadius:8,textAlign:"center"}}>
      <div style={{fontSize:18,fontWeight:800,color,fontFamily:S}}>{value.toLocaleString()}</div>
      <div style={{fontSize:9,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginTop:2}}>{label}</div>
    </div>
  );
}

function NewCampaignTab({ onDone }) {
  const [name, setName] = useState("");
  const [offerLabel, setOfferLabel] = useState("$10/unit Botox + Free Red Light Therapy for Anti-Aging");
  const [offerDetails, setOfferDetails] = useState("Nova Med Spa is offering $10 per unit of Botox (lowest price on Long Island) PLUS a complimentary Red Light Therapy session for anti-aging benefits. Available to first-time clients.");
  const [messageStyle, setMessageStyle] = useState("ai");
  const [fixedMessage, setFixedMessage] = useState("");
  const [dailyLimit, setDailyLimit] = useState(250);
  const [sendHour, setSendHour] = useState(10);

  const [csvText, setCsvText] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [parsedHeader, setParsedHeader] = useState([]);

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Parse CSV on change (debounced)
  useEffect(() => {
    if (!csvText.trim()) { setParsedRows([]); setParsedHeader([]); return; }
    const t = setTimeout(() => {
      try {
        const out = parseCsv(csvText);
        if (out.rows) { setParsedRows(out.rows); setParsedHeader(out.header); }
      } catch (e) { console.warn("Parse failed:", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [csvText]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const rdr = new FileReader();
    rdr.onload = () => setCsvText(String(rdr.result || ""));
    rdr.readAsText(f);
  };

  // Compute summary
  const summary = useMemo(() => {
    if (parsedRows.length === 0) return null;
    const extracted = parsedRows.map(extractFromRow);
    const withPhone = extracted.filter(r => r.phone);
    return {
      total: parsedRows.length,
      with_phone: withPhone.length,
      no_phone: parsedRows.length - withPhone.length,
      extracted,
    };
  }, [parsedRows]);

  const launch = async () => {
    if (!name.trim()) { alert("Campaign name is required"); return; }
    if (!offerLabel.trim()) { alert("Offer label is required"); return; }
    if (!summary || summary.with_phone === 0) { alert("Upload or paste a list with at least one valid phone"); return; }

    setImporting(true);
    setImportResult(null);

    try {
      // 1. Create campaign
      const { data: { user } } = await supabase.auth.getUser();
      const { data: created, error: campErr } = await supabase.from("cold_outreach_campaigns").insert({
        name: name.trim(),
        offer_label: offerLabel.trim(),
        offer_details: offerDetails || null,
        message_style: messageStyle,
        fixed_message: messageStyle === "fixed" ? fixedMessage : null,
        daily_limit: +dailyLimit || 250,
        send_hour_et: +sendHour || 10,
        active: true,
        paused: false,
        created_by: user?.id || null,
        total_recipients: summary.with_phone,
      }).select().single();
      if (campErr) throw campErr;

      // 2. Fetch existing phones to dedup against (paginated)
      const fetchAllPhones = async (table) => {
        const phones = new Set();
        let from = 0;
        const PAGE = 1000;
        while (true) {
          const { data } = await supabase.from(table).select("phone").range(from, from + PAGE - 1);
          if (!data || data.length === 0) break;
          data.forEach(r => {
            const p = (r.phone || "").replace(/\D/g, "").slice(-10);
            if (p.length === 10) phones.add(p);
          });
          if (data.length < PAGE) break;
          from += PAGE;
        }
        return phones;
      };

      // Also fetch phones from PRIOR cold outreach recipients so the same
      // person doesn't get hit by two different campaigns. Cold outreach is
      // one-shot — if we already messaged them once, sending again degrades
      // the brand. This fixes Ingrid getting TWO different cold messages.
      const [clientPhones, leadPhones, dncPhones, priorOutreachPhones] = await Promise.all([
        fetchAllPhones("clients"),
        fetchAllPhones("leads"),
        fetchAllPhones("dnc_list"),
        fetchAllPhones("cold_outreach_recipients"),
      ]);

      // 3. Insert recipients in batches with dedup reasons
      const seenInList = new Set();
      const recipients = summary.extracted.map((r, idx) => {
        let eligible = true;
        let dedup_reason = null;
        if (!r.phone) {
          eligible = false; dedup_reason = "no_phone";
        } else {
          const last10 = r.phone.replace(/\D/g, "").slice(-10);
          if (seenInList.has(last10)) {
            eligible = false; dedup_reason = "duplicate_in_list";
          } else if (clientPhones.has(last10)) {
            eligible = false; dedup_reason = "existing_client";
          } else if (leadPhones.has(last10)) {
            eligible = false; dedup_reason = "existing_lead";
          } else if (dncPhones.has(last10)) {
            eligible = false; dedup_reason = "dnc";
          } else if (priorOutreachPhones.has(last10)) {
            eligible = false; dedup_reason = "already_in_prior_campaign";
          }
          seenInList.add(last10);
        }
        return {
          campaign_id: created.id,
          full_name: r.full_name,
          first_name: r.first_name,
          last_name: r.last_name,
          phone: r.phone || `unknown_${idx}`,
          email: r.email,
          raw_row: parsedRows[idx],
          dedup_reason,
          eligible,
          status: eligible ? "queued" : "skipped",
        };
      });

      // Insert in chunks of 500
      const CHUNK = 500;
      let inserted = 0;
      for (let i = 0; i < recipients.length; i += CHUNK) {
        const slice = recipients.slice(i, i + CHUNK);
        const { error } = await supabase.from("cold_outreach_recipients").insert(slice);
        if (error) {
          console.error("Insert chunk error:", error);
          throw error;
        }
        inserted += slice.length;
        setImportResult({ inserting: true, inserted, total: recipients.length });
      }

      // Aggregate
      const eligibleCount = recipients.filter(r => r.eligible).length;
      const skippedReasons = {};
      recipients.filter(r => !r.eligible).forEach(r => {
        skippedReasons[r.dedup_reason] = (skippedReasons[r.dedup_reason] || 0) + 1;
      });

      setImportResult({
        ok: true,
        campaign_id: created.id,
        total: recipients.length,
        eligible: eligibleCount,
        skipped: recipients.length - eligibleCount,
        skipped_breakdown: skippedReasons,
      });
    } catch (e) {
      console.error("Launch failed:", e);
      setImportResult({ ok: false, error: e.message || String(e) });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
        <div style={{fontSize:14,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:14}}>1️⃣ Campaign Details</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div style={{gridColumn:"1 / -1"}}>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Campaign Name *</label>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder='e.g. "List 1 — AestheticRecord Cold Leads"' style={inp}/>
          </div>
          <div style={{gridColumn:"1 / -1"}}>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Offer Label *</label>
            <input value={offerLabel} onChange={e=>setOfferLabel(e.target.value)} style={inp}/>
            <div style={{fontSize:10,color:C.muted,marginTop:4}}>Short headline of the offer — what the AI bot will reference</div>
          </div>
          <div style={{gridColumn:"1 / -1"}}>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Offer Details (for AI context)</label>
            <textarea value={offerDetails} onChange={e=>setOfferDetails(e.target.value)} rows={3} style={{...inp,resize:"vertical"}} placeholder='Full description of the offer that helps the AI write good, accurate messages…'/>
          </div>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Daily Limit</label>
            <input type="number" value={dailyLimit} onChange={e=>setDailyLimit(e.target.value)} min="1" max="2000" style={inp}/>
          </div>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Send Hour (ET)</label>
            <input type="number" value={sendHour} onChange={e=>setSendHour(e.target.value)} min="6" max="20" style={inp}/>
            <div style={{fontSize:10,color:C.muted,marginTop:4}}>24h format (10 = 10 AM, 14 = 2 PM)</div>
          </div>
        </div>
      </div>

      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
        <div style={{fontSize:14,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:14}}>2️⃣ Upload List</div>
        <div style={{fontSize:11,color:C.muted,marginBottom:10}}>
          CSV or paste from Excel. Expected columns: <code>name</code>, <code>phone</code>, <code>email</code> (case-insensitive, variations like first_name/last_name also work).
        </div>
        <input type="file" accept=".csv,.txt,.tsv" onChange={onFile} style={{marginBottom:10,fontSize:12}}/>
        <textarea
          value={csvText}
          onChange={e=>setCsvText(e.target.value)}
          rows={8}
          placeholder="…or paste rows here. First line should be headers."
          style={{...inp,resize:"vertical",fontFamily:"monospace",fontSize:11}}
        />

        {summary && (
          <div style={{marginTop:14,padding:"12px 14px",background:C.goldBg,border:`1px solid ${C.borderGold}`,borderRadius:8}}>
            <div style={{fontSize:13,fontWeight:700,color:C.goldD,marginBottom:6}}>📊 List Preview</div>
            <div style={{fontSize:12,color:C.text}}>
              <strong>{summary.total.toLocaleString()}</strong> total rows ·
              <strong style={{color:C.green,marginLeft:8}}>{summary.with_phone.toLocaleString()} with valid phone</strong>
              {summary.no_phone > 0 && <span style={{color:C.red,marginLeft:8}}> · {summary.no_phone} rows missing phone</span>}
            </div>
            <div style={{fontSize:11,color:C.muted,marginTop:6}}>
              Columns detected: <code>{parsedHeader.join(", ")}</code>
            </div>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>
              Sample first 3: {summary.extracted.slice(0, 3).map(r => `${r.first_name || r.full_name || "?"} ${r.phone || "(no phone)"}`).join(" · ")}
            </div>
          </div>
        )}
      </div>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <div style={{fontSize:12,color:C.muted}}>
          🛡 On launch, we'll auto-skip any phone already in <strong>clients</strong>, <strong>leads</strong>, or <strong>DNC list</strong>.
        </div>
        <button
          onClick={launch}
          disabled={importing || !summary || summary.with_phone === 0 || !name.trim()}
          style={{
            padding:"12px 26px",
            background: (importing || !summary || summary.with_phone === 0 || !name.trim()) ? C.light : `linear-gradient(135deg,${C.gold},${C.goldD})`,
            color:(importing || !summary || summary.with_phone === 0 || !name.trim()) ? C.muted : "#fff",
            border:"none", borderRadius:8, fontSize:13, fontWeight:800, cursor:(importing || !summary || summary.with_phone === 0 || !name.trim()) ? "not-allowed" : "pointer", fontFamily:F,
          }}
        >
          {importing ? (importResult?.inserting ? `Importing… ${importResult.inserted}/${importResult.total}` : "Importing…") : "🚀 Launch Campaign"}
        </button>
      </div>

      {importResult && importResult.ok && (
        <div style={{padding:18,background:C.greenL,border:`1px solid ${C.green}40`,borderRadius:10}}>
          <div style={{fontSize:14,fontWeight:800,color:C.green,marginBottom:8}}>✅ Campaign created!</div>
          <div style={{fontSize:12,color:C.text}}>
            • {importResult.total.toLocaleString()} total rows imported<br/>
            • <strong>{importResult.eligible.toLocaleString()}</strong> eligible (will receive messages)<br/>
            • {importResult.skipped.toLocaleString()} skipped (deduped)
          </div>
          {importResult.skipped > 0 && (
            <div style={{fontSize:11,color:C.muted,marginTop:8}}>
              Breakdown of skipped: {Object.entries(importResult.skipped_breakdown).map(([k,v]) => `${k}: ${v}`).join(" · ")}
            </div>
          )}
          <div style={{marginTop:12}}>
            <button onClick={onDone} style={{padding:"8px 18px",background:`linear-gradient(135deg,${C.green},#1e6b3f)`,color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>View Campaign →</button>
          </div>
        </div>
      )}
      {importResult && !importResult.ok && (
        <div style={{padding:14,background:C.redL,border:`1px solid ${C.red}40`,borderRadius:10,color:C.red,fontSize:12,fontWeight:600}}>
          ❌ Failed: {importResult.error}
        </div>
      )}
    </div>
  );
}
